import { z } from 'zod';

import { indexSearchSchema, listParams } from '@nucleus-commerce/admin-kit/index-table';

import type { MemberListParams } from '../api/schedules-api';
import { MEMBER_FILTER_FIELDS } from '../components/schedule-filters';

export const SCHEDULE_PAGE_TABS = ['overview', 'pool', 'runs', 'upcoming', 'settings'] as const;

export type SchedulePageTab = (typeof SCHEDULE_PAGE_TABS)[number];

/**
 * `?tab=` on a schedule, the pool table's list state (`q`, `cursor`, `limit`, `pinned`,
 * `excluded`, `source`), `run` (a run whose items are open; the bell links here with it) and
 * `preview=1` (open the next-batch preview, as the create page does after saving).
 */
export const schedulePageSearchSchema = indexSearchSchema(MEMBER_FILTER_FIELDS).extend({
  tab: z.enum(SCHEDULE_PAGE_TABS).optional().catch(undefined),
  run: z.string().min(1).max(64).optional().catch(undefined),
  preview: z
    .union([z.literal(1), z.literal('1'), z.literal(true)])
    .transform(() => true as const)
    .optional()
    .catch(undefined),
});

export type SchedulePageSearch = z.infer<typeof schedulePageSearchSchema>;

/** A `run` link opens the Runs tab even without `tab`. */
export function schedulePageTab(search: Pick<SchedulePageSearch, 'tab' | 'run'>): SchedulePageTab {
  return search.tab ?? (search.run ? 'runs' : 'overview');
}

/** The pool's API params; `pinned` / `excluded` travel as booleans. */
export function memberListParams(search: Readonly<Record<string, unknown>>): MemberListParams {
  const { pinned, excluded, source, q, cursor, limit } = listParams(search, MEMBER_FILTER_FIELDS);
  return {
    ...(typeof q === 'string' ? { q } : {}),
    ...(typeof cursor === 'string' ? { cursor } : {}),
    ...(typeof limit === 'number' ? { limit } : {}),
    ...(typeof source === 'string' ? { source } : {}),
    ...(pinned === 'true' || pinned === 'false' ? { pinned: pinned === 'true' } : {}),
    ...(excluded === 'true' || excluded === 'false' ? { excluded: excluded === 'true' } : {}),
  };
}
